import { faCircleInfo, faHeart } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import { HEALTH_COLORS, barHeight } from './constants';

import { PoolRow } from '@/services/preferredPoolsAPI';
import styled from '@/styles';

// ui-prototype/preferred-worker-pool/client-detail.html #barChart. One bar per
// pool — ALL pools, not just the chip subset (plan edge #21). The server
// returns pools ascending by active_member_count, which is the chart's
// required order, so no client-side sort. Width is explicit: the parent Stack
// doesn't stretch children, and a shrink-to-fit chart collapsed the bars into
// a sliver and dragged the hint left with it (GRAVY-10370 items 3/4).

const BAR_MAX_WIDTH = 64;

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  position: 'relative',
  width: '100%',
});

const HintRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 4,
  fontSize: '12px',
  color: '#5B5B5B',
  paddingBottom: 4,
});

const Chart = styled('div', {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  borderBottom: '1px solid #DADADA',
  height: 48,
});

const BarButton = styled('button', {
  all: 'unset',
  flex: 1,
  // Prototype bars split a full 12-pool row (~55px each); with only a pool or
  // two, uncapped flex:1 bars balloon to hundreds of px (GRAVY-10370 item 4).
  maxWidth: BAR_MAX_WIDTH,
  height: '100%',
  display: 'flex',
  alignItems: 'flex-end',
  cursor: 'pointer',
});

const Bar = styled('div', {
  width: '100%',
  borderRadius: '4px 4px 0 0',
  opacity: 0.7,
  variants: {
    selected: {
      true: {
        opacity: 1,
        border: '2px solid #2E8722',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.12)',
      },
    },
  },
  '&:hover': { opacity: 1 },
});

// ui-prototype #barScale — the health legend under the bars: "< 5" / "5+"
// markers on the color boundaries plus the heart at the right end. The cells
// mirror the chart's flex layout exactly (same gap, flex, max-width), so each
// boundary label lands on its bar edge without measuring the DOM.
//
// Each label hangs off the LEFT edge of its boundary cell (`end`), as in the
// prototype. When there is no amber pool the red|amber and amber|green
// boundaries are the same bar edge, and two `end` labels draw on top of each
// other ("<55", QA screenshot 2026-08-27); the prototype never hits this
// because its demo data always has a yellow pool. In that case "5+" swaps to
// the RIGHT edge of the shared boundary (`start`), under the first green bar.
const ScaleRow = styled('div', {
  display: 'flex',
  gap: 8,
  position: 'relative',
  height: 16,
});

const ScaleCell = styled('div', {
  flex: 1,
  maxWidth: BAR_MAX_WIDTH,
  position: 'relative',
});

const ScaleLabel = styled('span', {
  position: 'absolute',
  top: 0,
  fontSize: '12px',
  color: '#5B5B5B',
  whiteSpace: 'nowrap',
  variants: {
    align: {
      end: { left: -4, transform: 'translateX(-100%)' },
      start: { left: 4, transform: 'none' },
    },
  },
});

const Tooltip = styled('div', {
  position: 'absolute',
  bottom: 56,
  transform: 'translateX(-50%)',
  background: '#FCFCFC',
  border: '1px solid #DADADA',
  boxShadow: '0 -2px 4px rgba(0,0,0,0.12)',
  borderRadius: 8,
  height: 32,
  padding: '0 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: '14px',
  color: '#1C1C1C',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  zIndex: 60,
});

const Dot = styled('span', {
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
});

type Props = {
  pools: PoolRow[];
  selectedPoolId: number | null;
  onSelect: (poolId: number) => void;
};

type Hovered = { pool: PoolRow; left: number };

const PoolHealthChart = ({ pools, selectedPoolId, onSelect }: Props) => {
  const [hovered, setHovered] = useState<Hovered | null>(null);

  const redCount = pools.filter((pool) => pool.health === 'red').length;
  const belowGreenCount = pools.filter(
    (pool) => pool.health !== 'green',
  ).length;
  const showLow = redCount > 0 && redCount < pools.length;
  const showHigh = belowGreenCount > 0 && belowGreenCount < pools.length;
  const sharedBoundary = showLow && showHigh && redCount === belowGreenCount;
  const highAlign = sharedBoundary ? 'start' : 'end';

  return (
    <Wrapper>
      <HintRow>
        <FontAwesomeIcon
          icon={faCircleInfo}
          style={{ width: 14, height: 14 }}
        />
        Hover any bar to view more info
      </HintRow>
      <Chart data-testid="barChart">
        {pools.map((pool) => (
          <BarButton
            key={pool.id}
            aria-label={pool.skill.name ?? `Pool ${pool.id}`}
            data-health={pool.health}
            type="button"
            onClick={() => onSelect(pool.id)}
            onMouseEnter={(event) => {
              const el = event.currentTarget;
              setHovered({
                pool,
                left: el.offsetLeft + el.offsetWidth / 2,
              });
            }}
            onMouseLeave={() => setHovered(null)}
          >
            <Bar
              selected={pool.id === selectedPoolId}
              style={{
                height: barHeight(pool.active_member_count),
                background: HEALTH_COLORS[pool.health],
              }}
            />
          </BarButton>
        ))}
      </Chart>
      <ScaleRow data-testid="barScale">
        {pools.map((pool, index) => (
          <ScaleCell key={pool.id}>
            {showLow && index === redCount && (
              <ScaleLabel
                align="end"
                data-align="end"
                data-testid="barScaleLow"
              >
                &lt; 5
              </ScaleLabel>
            )}
            {showHigh && index === belowGreenCount && (
              <ScaleLabel
                align={highAlign}
                data-align={highAlign}
                data-testid="barScaleHigh"
              >
                5+
              </ScaleLabel>
            )}
          </ScaleCell>
        ))}
        <FontAwesomeIcon
          icon={faHeart}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: 14,
            height: 14,
            color: '#44A735',
          }}
        />
      </ScaleRow>
      {hovered && (
        <Tooltip data-testid="barTooltip" style={{ left: hovered.left }}>
          <Dot
            data-testid="barTooltipDot"
            style={{ background: HEALTH_COLORS[hovered.pool.health] }}
          />
          <b data-testid="barTooltipName">{hovered.pool.skill.name}</b>
          <span data-testid="barTooltipCount">
            ({hovered.pool.active_member_count}/{hovered.pool.target_depth})
          </span>
        </Tooltip>
      )}
    </Wrapper>
  );
};

export default PoolHealthChart;
